import type { Event } from '@cocoonspace/sdk-js/types/event'
import type { ComponentProps } from 'react'
import { useListInvoices } from '@cocoonspace/sdk-js/domains/invoices/invoices'
import { Button, buttonVariants } from '@cocoonspace/ui/components/button'
import { Card } from '@cocoonspace/ui/components/card'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '@cocoonspace/ui/components/dialog'
import { Skeleton } from '@cocoonspace/ui/components/skeleton'
import { Table, TableBody } from '@cocoonspace/ui/components/table'
import { cn } from '@cocoonspace/ui/lib/utils'
import { Plural, useLingui } from '@lingui/react/macro'
import { ArrowRightIcon, FileTextIcon, PencilIcon } from 'lucide-react'
import dynamic from 'next/dynamic'
import { LocalizedLink as Link } from '~/components/localized-link'

import { InvoiceTableRow } from '~/domains/account/components/invoices/invoice-table-row'
import { InvoicesTableHeader } from '~/domains/account/components/invoices/invoices-table-header'
import { useUserContext } from '~/providers/user-provider'
import type { EventItem } from '../my-events/use-all-events.hook'

const OrganizerPanel = dynamic(
	() => import('../event-organizer-panel').then((mod) => mod.OrganizerPanel),
	{ ssr: false },
)

const UpdateAction = ({
	event,
	protectSchedulerEvent,
}: {
	event: EventItem
	protectSchedulerEvent: boolean
}) => {
	const { t } = useLingui()

	if (!event.isOrganizer) return null

	return (
		<OrganizerPanel
			event={event.originalEvent as Event}
			protectSchedulerEvent={protectSchedulerEvent}
		>
			<Button
				variant='outline'
				className='flex-1 gap-1 rounded'
				size='sm'
			>
				<PencilIcon />
				{t`Modifier`}
			</Button>
		</OrganizerPanel>
	)
}

const InvoiceDialogContent = ({ event }: { event: Pick<EventItem, 'id'> }) => {
	const { data: invoices } = useListInvoices({
		// @ts-expect-error TODO: Update swagger
		event: event.id,
	})

	return (
		<>
			<DialogHeader>
				<DialogTitle>
					<Plural
						value={invoices?.data.length}
						one='Voir la facture'
						other='Voir les factures'
					/>
				</DialogTitle>
			</DialogHeader>

			<Card className='flex flex-col divide-y overflow-hidden border'>
				<Table className='w-full'>
					<InvoicesTableHeader />

					<TableBody>
						{invoices?.data?.map((invoice) => (
							<InvoiceTableRow
								invoice={invoice}
								key={invoice.ref}
							/>
						))}
					</TableBody>
				</Table>
			</Card>
		</>
	)
}

export const PastActions = ({
	event,
}: {
	event: Pick<EventItem, 'id' | 'space' | 'isOrganizer'>
}) => {
	const { t } = useLingui()
	const { accountQuery } = useUserContext()

	return (
		<div className='mt-auto flex flex-row items-center justify-end gap-4'>
			{!accountQuery.data?.business && event.isOrganizer && (
				<Dialog>
					<DialogTrigger asChild>
						<Button
							className='flex-1'
							onClick={(e) => {
								e.stopPropagation()
								e.nativeEvent.preventDefault()
							}}
							size='sm'
							variant='outline'
						>
							<FileTextIcon />
							{t`Voir la facture`}
						</Button>
					</DialogTrigger>

					<DialogContent
						className='max-w-[70%]'
						onCloseAutoFocus={(e) => {
							e.preventDefault()
							e.stopImmediatePropagation()
						}}
					>
						<InvoiceDialogContent event={event} />
					</DialogContent>
				</Dialog>
			)}

			{event.space && (
				<Link
					className={buttonVariants({
						className: 'flex-1',
						size: 'sm',
						variant: 'outline',
					})}
					data-umami-event='eventCard-bookSame-button'
					href={`/${event.space.zone}/${event.space.slug}`}
				>
					{t`Réserver ce Cocoon`}
				</Link>
			)}
		</div>
	)
}

export const EventCardFooter = ({
	event,
	isPast,
	protectSchedulerEvent = false,
}: {
	event: EventItem
	isPast: boolean
	protectSchedulerEvent?: boolean
} & ComponentProps<'div'>) => {
	const { t } = useLingui()

	const isCocoonBooking = event.eventType === 'cocoon'
	const isBookingRequest = event.eventType === 'bookingRequest'
	const isRemoteBooking = isBookingRequest && event.location.type === 'remote'
	const calendarLink = event.googleCalendar.editLink

	if (isPast) return <PastActions event={event} />

	return (
		<div className='mt-auto flex flex-row items-center gap-4'>
			<UpdateAction
				event={event}
				protectSchedulerEvent={protectSchedulerEvent}
			/>

			{isCocoonBooking && (
				<Button
					variant='outlinePrimary'
					size='sm'
					className='flex-1'
					asChild
				>
					<Link href={`/events/${event.id}`}>
						{t`Voir l'évènement`}
						<ArrowRightIcon />
					</Link>
				</Button>
			)}

			{isRemoteBooking && calendarLink && (
				<Button
					variant='outlinePrimary'
					size='sm'
					className='flex-1'
					asChild
				>
					<Link
						href={calendarLink}
						target='_blank'
					>
						{t`Voir dans Google Calendar`}
						<ArrowRightIcon />
					</Link>
				</Button>
			)}
		</div>
	)
}

EventCardFooter.skeleton = (props: ComponentProps<'div'>) => (
	<div
		className={cn(
			'flex flex-row items-center justify-center gap-4',
			props.className,
		)}
	>
		<Skeleton className='h-[38px] w-[130px]' />
		<Skeleton className='h-[16px] w-[130px]' />
	</div>
)
